// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { proxyFetchPost } from '@/api/http';
import { isDesktop, isWeb } from '@/client/platform';
import { features } from '@/lib/edition';
import ProductPageFrame from '@/products/ProductPageFrame';
import {
  PRODUCTS,
  allRedirects,
  findVisibleProduct,
  type ProductDefinition,
} from '@/products/registry';
import { useAuthStore } from '@/store/authStore';
import {
  SETTINGS_SECTIONS,
  type SettingsSectionId,
} from '@/store/settingsStore';
import { Suspense, lazy, useEffect, useReducer } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import Layout from '@/components/Layout';
import PublicLayout from '@/components/Layout/PublicLayout';
import { LoginPage, SignUpPage } from './authPages';
import { warnAuthClearedForEnvironment } from '@/lib/authEnvironment';
import { SESSION_WINDOW_ROUTE } from '@/lib/sessionWindows';
import { LegacyRouteRedirect } from './LegacyRouteCompatibility';
import SubPageShellLayout from './SubPageShellLayout';
import WorkspaceSettingsRouteLayout from './WorkspaceSettingsRouteLayout';
// Lazy load page components
const Workspace = lazy(() => import('@/pages/Workspace'));
const Settings = lazy(() => import('@/pages/Settings'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const RemoteControl = lazy(() => import('@/pages/RemoteControl'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const OpenClawSetup = lazy(() => import('@/pages/OpenClawSetup'));
const Products = lazy(() => import('@/pages/Products'));

// One lazy page per registered product, built when this module loads.
const PRODUCT_PAGES = new Map(PRODUCTS.map((p) => [p.id, lazy(p.load)]));

/**
 * A product's page. Visibility is checked again on render, so a service an
 * admin switches off at runtime disappears without a reload.
 */
function ProductRoute({ product }: { product: ProductDefinition }) {
  const email = useAuthStore((state) => state.email);
  const Page = PRODUCT_PAGES.get(product.id)!;
  if (!findVisibleProduct(product.path, { email })) return <NotFound />;
  if (product.hidden) {
    return (
      <Suspense fallback={null}>
        <Page />
      </Suspense>
    );
  }
  return (
    <ProductPageFrame>
      <Suspense fallback={null}>
        <Page />
      </Suspense>
    </ProductPageFrame>
  );
}
const SessionWindow = lazy(() => import('@/pages/SessionWindow'));

const IS_LOCAL_MODE = import.meta.env.VITE_USE_LOCAL_PROXY === 'true';
const ENABLE_DESKTOP_REMOTE_CONTROL_FALLBACK = isDesktop();
// Stripe returns to the browser, so billing pages are web-only.
const BILLING_ROUTES = features.billing && isWeb();

interface AuthState {
  loading: boolean;
  isAuthenticated: boolean;
  initialized: boolean;
}

type AuthAction =
  | { type: 'INITIALIZE'; payload: { isAuthenticated: boolean } }
  | { type: 'LOGOUT' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        loading: false,
        isAuthenticated: action.payload.isAuthenticated,
        initialized: true,
      };
    case 'LOGOUT':
      return {
        loading: false,
        isAuthenticated: false,
        initialized: true,
      };
    default:
      return state;
  }
};

// Route guard: Check if user is logged in
const ProtectedRoute = () => {
  const location = useLocation();
  const [state, dispatch] = useReducer(authReducer, {
    loading: false,
    isAuthenticated: false,
    initialized: false,
  });

  const {
    token,
    localProxyValue,
    logout,
    setAuth,
    setLocalProxyValue,
    setInitState,
    setIsFirstLaunch,
    setModelType,
  } = useAuthStore();
  useEffect(() => {
    // Check VITE_USE_LOCAL_PROXY value on app startup
    if (token) {
      const currentProxyValue = import.meta.env.VITE_USE_LOCAL_PROXY || null;
      const storedProxyValue = localProxyValue;

      // If stored value exists and differs from current, logout
      if (storedProxyValue !== null && storedProxyValue !== currentProxyValue) {
        warnAuthClearedForEnvironment(['local']);
        logout();
        dispatch({ type: 'LOGOUT' });
        return;
      }
    }

    // Local mode: auto-login when no token
    if (IS_LOCAL_MODE && !token) {
      proxyFetchPost('/api/v1/user/auto-login', {})
        .then((data) => {
          if (data && data.token) {
            setAuth({ email: data.email, ...data });
            setLocalProxyValue(import.meta.env.VITE_USE_LOCAL_PROXY || null);
            setModelType('custom');
            setInitState('done');
            setIsFirstLaunch(false);
            dispatch({
              type: 'INITIALIZE',
              payload: { isAuthenticated: true },
            });
          } else {
            dispatch({
              type: 'INITIALIZE',
              payload: { isAuthenticated: false },
            });
          }
        })
        .catch(() => {
          dispatch({
            type: 'INITIALIZE',
            payload: { isAuthenticated: false },
          });
        });
      return;
    }

    dispatch({ type: 'INITIALIZE', payload: { isAuthenticated: !!token } });
  }, [
    token,
    localProxyValue,
    logout,
    setAuth,
    setLocalProxyValue,
    setInitState,
    setIsFirstLaunch,
    setModelType,
  ]);

  if (state.loading || !state.initialized) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-x-0 border-t-0 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (state.isAuthenticated) {
    return <Outlet />;
  }

  const redirect = `${location.pathname}${location.search}`;
  return (
    <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />
  );
};

/** Keep legacy Settings links working while Home is the canonical surface. */
function SettingsRouteRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sectionFromUrl = searchParams.get('section');
  const isLegacySettingsSection = SETTINGS_SECTIONS.includes(
    sectionFromUrl as SettingsSectionId
  );

  if (sectionFromUrl !== 'spaces') {
    searchParams.set('section', 'settings');
    if (
      sectionFromUrl &&
      isLegacySettingsSection &&
      sectionFromUrl !== 'settings'
    ) {
      searchParams.set('tab', sectionFromUrl);
    }
  }

  return (
    <Navigate
      to={`/home?${searchParams.toString()}`}
      replace
      state={location.state}
    />
  );
}

// Main route configuration
const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/signup" element={<SignUpPage />} />
    {ENABLE_DESKTOP_REMOTE_CONTROL_FALLBACK ? (
      <Route path="/remote-control/:sessionId" element={<RemoteControl />} />
    ) : null}
    {BILLING_ROUTES ? (
      <Route element={<PublicLayout />}>
        <Route path="/pricing" element={<Pricing />} />
      </Route>
    ) : null}
    <Route element={<ProtectedRoute />}>
      {/* A Session in its own window: no app shell, sidebar or top bar. */}
      <Route path={SESSION_WINDOW_ROUTE} element={<SessionWindow />} />
      <Route element={<Layout />}>
        <Route
          element={<WorkspaceSettingsRouteLayout workspace={<Workspace />} />}
        >
          <Route index element={null} />
          <Route path="/home" element={<Settings />} />
        </Route>
        <Route path="/settings" element={<SettingsRouteRedirect />} />
        {/* Sub-pages share the Home / Settings rail so any page is one click from any other. */}
        <Route element={<SubPageShellLayout />}>
          {features.gateway ? (
            <Route path="/openclaw-setup" element={<OpenClawSetup />} />
          ) : null}
          {features.products ? (
            <>
              <Route path="/products" element={<Products />} />
              {PRODUCTS.map((product) => (
                <Route
                  key={product.id}
                  path={product.path}
                  element={<ProductRoute product={product} />}
                />
              ))}
            </>
          ) : null}
        </Route>
        {features.gateway ? (
          <Route
            path="/gateway"
            element={
              <Navigate to="/home?section=settings&tab=gateway" replace />
            }
          />
        ) : null}
        {features.products ? (
          <>
            {allRedirects().map(({ from, to }) => (
              <Route
                key={from}
                path={from}
                element={<Navigate to={to} replace />}
              />
            ))}
          </>
        ) : null}
        {BILLING_ROUTES ? (
          <Route
            path="/dashboard"
            element={
              <Navigate to="/home?section=settings&tab=billing" replace />
            }
          />
        ) : null}
        <Route
          path="/history"
          element={<LegacyRouteRedirect kind="history" />}
        />
        <Route
          path="/workspace-configuration"
          element={<LegacyRouteRedirect kind="workspace-configuration" />}
        />
        <Route
          path="/workspace-bundles/install"
          element={<LegacyRouteRedirect kind="workspace-bundle-install" />}
        />
        <Route
          path="/agent-plugins/import"
          element={<LegacyRouteRedirect kind="agent-plugin-import" />}
        />
        <Route
          path="/setting"
          element={<Navigate to="/home?section=settings" replace />}
        />
        <Route
          path="/setting/*"
          element={<Navigate to="/home?section=settings" replace />}
        />
      </Route>
    </Route>
    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default AppRoutes;
